import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ClipboardList, 
  Plus,
  Calendar,
  Clock,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  PenLine,
  User,
  FileText,
  Pencil,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecordsPage() {
  const [searchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [signatureCache, setSignatureCache] = useState({});
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    client_id: searchParams.get('client_id') || '',
    service_date: '',
    service_type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/support-records`),
        axios.get(`${API}/clients`)
      ]);
      setRecords(recordsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const actualValue = value === 'all' ? '' : value;
    setFilters(prev => ({ ...prev, [field]: actualValue }));
  };

  const toggleExpand = async (recordId) => {
    if (expandedId === recordId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(recordId);
    // 署名データを取得（キャッシュになければ）
    const record = records.find(r => r.id === recordId);
    if (record?.has_signature && !signatureCache[recordId]) {
      try {
        const res = await axios.get(`${API}/support-records/${recordId}/signature`);
        setSignatureCache(prev => ({ ...prev, [recordId]: res.data.signature_data }));
      } catch (e) {
        console.error('署名データ取得失敗', e);
      }
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('この支援記録を削除しますか？')) return;
    try {
      await axios.delete(`${API}/support-records/${recordId}`);
      setExpandedId(null);
      fetchData();
    } catch (e) {
      alert('削除に失敗しました: ' + (e.response?.data?.detail || e.message));
    }
  };


  const filteredRecords = records.filter(record => {
    if (filters.client_id && record.client_id !== filters.client_id) return false;
    if (filters.service_date && record.service_date !== filters.service_date) return false;
    if (filters.service_type && record.service_type !== filters.service_type) return false;
    return true;
  });

  const serviceTypes = ['日中一時支援', '移動支援', '地域生活支援'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="records-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            支援記録
          </h1>
          <p className="text-muted-foreground mt-1">
            記録数: {records.length}件
          </p>
        </div>
        <Link to="/records/new">
          <Button className="btn-active" data-testid="add-record-button">
            <Plus size={18} className="mr-2" />
            新規記録
          </Button>
        </Link>
      </div>

      {/* フィルター */}
      <Card className="card-surface">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Select 
                value={filters.client_id} 
                onValueChange={(value) => handleFilterChange('client_id', value)}
              >
                <SelectTrigger data-testid="filter-client-select">
                  <SelectValue placeholder="利用者で絞り込み" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての利用者</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Input
                type="date"
                value={filters.service_date}
                onChange={(e) => handleFilterChange('service_date', e.target.value)}
                data-testid="filter-date-input"
              />
            </div>
            <div>
              <Select 
                value={filters.service_type} 
                onValueChange={(value) => handleFilterChange('service_type', value)}
              >
                <SelectTrigger data-testid="filter-service-select">
                  <SelectValue placeholder="サービス種別" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべてのサービス</SelectItem>
                  {serviceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline"
              onClick={() => setFilters({ client_id: '', service_date: '', service_type: '' })}
              data-testid="clear-filters-button"
            >
              フィルターをクリア
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 記録一覧 */}
      {filteredRecords.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <ClipboardList size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {records.length === 0 ? '支援記録がありません' : '条件に一致する記録がありません'}
            </p>
            <Link to="/records/new">
              <Button className="mt-4" variant="outline">
                最初の記録を作成する
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredRecords.map((record) => {
            const isExpanded = expandedId === record.id;
            return (
              <Card 
                key={record.id} 
                className={`card-surface overflow-hidden transition-all cursor-pointer ${isExpanded ? 'ring-2 ring-primary/30' : ''}`}
                data-testid={`record-row-${record.id}`}
              >
                <div 
                  className="p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
                  onClick={() => toggleExpand(record.id)}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} className="text-muted-foreground shrink-0" />
                      <span className="font-medium">{record.service_date}</span>
                    </div>
                    <span className="font-medium truncate">{record.client_name}</span>
                    <span className={
                      record.service_type === '日中一時支援' ? 'badge-success' :
                      record.service_type === '移動支援' ? 'badge-warning' : 'badge-neutral'
                    }>
                      {record.service_type}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock size={14} />
                      <span>{record.start_time}-{record.end_time}</span>
                      <span>({record.duration_minutes}分)</span>
                    </div>
                    <span className="text-sm text-muted-foreground">{record.staff_name}</span>
                    {record.has_signature && (
                      <CheckCircle size={18} className="text-emerald-500 shrink-0" />
                    )}
                  </div>
                  {isExpanded ? <ChevronUp size={20} className="text-muted-foreground shrink-0" /> : <ChevronDown size={20} className="text-muted-foreground shrink-0" />}
                </div>

                {isExpanded && (
                  <div className="border-t border-border px-4 py-4 bg-secondary/10 space-y-4" data-testid={`record-detail-${record.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <User size={16} className="text-primary" />
                          <span className="text-sm font-semibold text-muted-foreground">利用者</span>
                        </div>
                        <p className="font-medium">{record.client_name}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Clock size={16} className="text-primary" />
                          <span className="text-sm font-semibold text-muted-foreground">支援時間</span>
                        </div>
                        <p>{record.service_date} {record.start_time} ~ {record.end_time}（{record.duration_minutes}分）</p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={16} className="text-primary" />
                        <span className="text-sm font-semibold text-muted-foreground">支援内容</span>
                      </div>
                      <p className="whitespace-pre-wrap bg-white rounded-lg p-3 border border-border text-sm">
                        {record.content || '（内容なし）'}
                      </p>
                    </div>

                    {record.notes && (
                      <div>
                        <span className="text-sm font-semibold text-muted-foreground">備考</span>
                        <p className="whitespace-pre-wrap text-sm mt-1">{record.notes}</p>
                      </div>
                    )}

                    {record.has_signature && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <PenLine size={16} className="text-primary" />
                          <span className="text-sm font-semibold text-muted-foreground">電子サイン</span>
                        </div>
                        {signatureCache[record.id] ? (
                          <div className="bg-white rounded-lg p-3 border border-border inline-block">
                            <img src={signatureCache[record.id]} alt="署名" className="max-h-20" />
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">読み込み中...</p>
                        )}
                      </div>
                    )}

                    {/* 編集・削除ボタン */}
                    <div className="flex gap-2 pt-2 border-t border-border">
                      <Link to={`/records/${record.id}/edit`}>
                        <Button size="sm" variant="outline" data-testid={`edit-record-${record.id}`}>
                          <Pencil size={14} className="mr-1" />編集
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteRecord(record.id)} data-testid={`delete-record-${record.id}`}>
                        <Trash2 size={14} className="mr-1" />削除
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
